import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import type { ComposerThreadDraftState } from "./composerDraftStore";

type ThreadDrafts = Record<string, ComposerThreadDraftState>;

function hasDraftContent(draft: ComposerThreadDraftState): boolean {
  return Boolean(
    draft.prompt.trim() ||
    draft.images.length ||
    draft.terminalContexts.length ||
    draft.persistedAttachments.length ||
    draft.stagedSubmission,
  );
}

export function moveThreadDraft(
  drafts: ThreadDrafts,
  source: ScopedThreadRef,
  destination: ScopedThreadRef,
): ThreadDrafts | null {
  const sourceKey = scopedThreadKey(source);
  const destinationKey = scopedThreadKey(destination);
  if (sourceKey === destinationKey) return drafts;
  const sourceDraft = drafts[sourceKey];
  if (!sourceDraft) return drafts;
  const target = drafts[destinationKey];
  if (target && hasDraftContent(target) && !sameLogicalDraft(sourceDraft, target)) return null;
  const moved = {
    ...drafts,
    [destinationKey]: {
      ...sourceDraft,
      ...handoffSettings(sourceDraft, target),
    },
  };
  delete moved[sourceKey];
  return moved;
}

function sameLogicalDraft(
  source: ComposerThreadDraftState,
  target: ComposerThreadDraftState,
): boolean {
  const sourceSubmission = source.stagedSubmission;
  if (!sourceSubmission) return false;
  if (!submissionHasIdentity(sourceSubmission)) return false;
  if (!currentContentMatchesSubmission(source, sourceSubmission)) return false;
  if (!targetSubmissionMatches(sourceSubmission, target.stagedSubmission)) return false;
  return currentDraftsMatch(source, target);
}

function submissionHasIdentity(
  submission: NonNullable<ComposerThreadDraftState["stagedSubmission"]>,
): boolean {
  return Boolean(submission.commandId) && Boolean(submission.messageId);
}

function currentContentMatchesSubmission(
  draft: ComposerThreadDraftState,
  submission: NonNullable<ComposerThreadDraftState["stagedSubmission"]>,
): boolean {
  return [
    draft.prompt === submission.prompt,
    sameIds(draft.persistedAttachments, submission.attachments),
    sameIds(draft.terminalContexts, submission.terminalContexts),
  ].every(Boolean);
}

function targetSubmissionMatches(
  source: NonNullable<ComposerThreadDraftState["stagedSubmission"]>,
  target: ComposerThreadDraftState["stagedSubmission"],
): boolean {
  if (!target) return true;
  return sameStagedSubmission(source, target);
}

function currentDraftsMatch(
  source: ComposerThreadDraftState,
  target: ComposerThreadDraftState,
): boolean {
  return [
    source.prompt === target.prompt,
    source.lastGeneratedPullRequestPrompt === target.lastGeneratedPullRequestPrompt,
    sameSerializableItems(source.images, target.images),
    sameStrings(source.nonPersistedImageIds, target.nonPersistedImageIds),
    source.nonPersistedImageIds.length === 0,
    sameSerializableItems(source.persistedAttachments, target.persistedAttachments),
    sameSerializableItems(source.terminalContexts, target.terminalContexts),
    sameModelSelections(source.modelSelectionByProvider, target.modelSelectionByProvider),
    source.activeProvider === target.activeProvider,
    source.runtimeMode === target.runtimeMode,
    source.interactionMode === target.interactionMode,
  ].every(Boolean);
}

function sameStagedSubmission(
  source: NonNullable<ComposerThreadDraftState["stagedSubmission"]>,
  target: NonNullable<ComposerThreadDraftState["stagedSubmission"]>,
): boolean {
  return [
    source.commandId === target.commandId && source.messageId === target.messageId,
    source.prompt === target.prompt,
    sameIds(source.attachments, target.attachments),
    sameIds(source.terminalContexts, target.terminalContexts),
    compatibleOptionalSetting(source.modelSelection, target.modelSelection),
    compatibleOptionalSetting(source.runtimeMode, target.runtimeMode),
    compatibleOptionalSetting(source.interactionMode, target.interactionMode),
    compatibleExecutionIntent(source.executionIntent, target.executionIntent),
  ].every(Boolean);
}

function compatibleOptionalSetting<T>(source: T | undefined, target: T | undefined): boolean {
  if (source === undefined || target === undefined) return true;
  return JSON.stringify(source) === JSON.stringify(target);
}

function compatibleExecutionIntent(
  source: { readonly runtimeId: string; readonly intentId?: string } | undefined,
  target: { readonly runtimeId: string; readonly intentId?: string } | undefined,
): boolean {
  if (!source || !target) return true;
  return source.runtimeId === target.runtimeId;
}

function sameIds(
  source: ReadonlyArray<{ readonly id: string }>,
  target: ReadonlyArray<{ readonly id: string }>,
): boolean {
  return (
    source.length === target.length && source.every((item, index) => item.id === target[index]?.id)
  );
}

function sameStrings(source: readonly string[], target: readonly string[]): boolean {
  return source.length === target.length && source.every((item, index) => item === target[index]);
}

function sameSerializableItems<T>(source: readonly T[], target: readonly T[]): boolean {
  return (
    source.length === target.length &&
    source.every((item, index) => JSON.stringify(item) === JSON.stringify(target[index]))
  );
}

function sameModelSelections(
  source: ComposerThreadDraftState["modelSelectionByProvider"],
  target: ComposerThreadDraftState["modelSelectionByProvider"],
): boolean {
  const sourceProviders = Object.keys(source).toSorted();
  const targetProviders = Object.keys(target).toSorted();
  if (!sameStrings(sourceProviders, targetProviders)) return false;
  return sourceProviders.every(
    (provider) =>
      JSON.stringify(source[provider as keyof typeof source]) ===
      JSON.stringify(target[provider as keyof typeof target]),
  );
}

function handoffSettings(
  source: ComposerThreadDraftState,
  target: ComposerThreadDraftState | undefined,
) {
  const destinationSettings = target ?? source;
  const preferred = source.stagedSubmission ? source : destinationSettings;
  const fallback = source.stagedSubmission ? destinationSettings : source;
  return {
    modelSelectionByProvider: {
      ...fallback.modelSelectionByProvider,
      ...preferred.modelSelectionByProvider,
    },
    activeProvider: preferred.activeProvider ?? fallback.activeProvider,
    runtimeMode: preferred.runtimeMode ?? fallback.runtimeMode,
    interactionMode: preferred.interactionMode ?? fallback.interactionMode,
  };
}
